import { supabase } from '../lib/supabase';

/**
 * Service for managing Fatou AI reservations
 */

// Fetch all reservations with optional filters
export const fetchReservations = async (filters = {}) => {
  try {
    let query = supabase?.from('fatou_reservations')?.select('*')?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }

    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }

    if (filters?.clientPhone) {
      query = query?.ilike('client_phone', `%${filters?.clientPhone}%`);
    }

    if (filters?.clientName) {
      query = query?.ilike('client_name', `%${filters?.clientName}%`);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching reservations:', error);
    throw error;
  }
};

// Fetch single reservation by ID
export const fetchReservationById = async (id) => {
  try {
    const { data, error } = await supabase?.from('fatou_reservations')?.select('*')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching reservation:', error);
    throw error;
  }
};

// Update reservation status
export const updateReservationStatus = async (id, status) => {
  try {
    const { data, error } = await supabase?.from('fatou_reservations')?.update({ status })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating reservation status:', error);
    throw error;
  }
};

// Update reservation
export const updateReservation = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('fatou_reservations')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating reservation:', error);
    throw error;
  }
};

// Mark confirmation email as sent
export const markEmailSent = async (id) => {
  try {
    const { data, error } = await supabase?.from('fatou_reservations')?.update({
        confirmation_email_sent: true,
        confirmation_email_sent_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error marking email as sent:', error);
    throw error;
  }
};

// Mark confirmation SMS as sent
export const markSmsSent = async (id) => {
  try {
    const { data, error } = await supabase?.from('fatou_reservations')?.update({
        confirmation_sms_sent: true,
        confirmation_sms_sent_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error marking SMS as sent:', error);
    throw error;
  }
};

// Convert reservation to shipment
export const convertToShipment = async (reservationId, shipmentId) => {
  try {
    const { data, error } = await supabase?.from('fatou_reservations')?.update({
        status: 'completed',
        converted_to_shipment_id: shipmentId,
        converted_at: new Date()?.toISOString()
      })?.eq('id', reservationId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error converting reservation to shipment:', error);
    throw error;
  }
};

// Delete reservation
export const deleteReservation = async (id) => {
  try {
    const { error } = await supabase?.from('fatou_reservations')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting reservation:', error);
    throw error;
  }
};

// Get reservation statistics
export const getReservationStats = async () => {
  try {
    const { data, error } = await supabase?.from('fatou_reservations')?.select('status, created_at');

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      pending: data?.filter(r => r?.status === 'pending')?.length || 0,
      confirmed: data?.filter(r => r?.status === 'confirmed')?.length || 0,
      completed: data?.filter(r => r?.status === 'completed')?.length || 0,
      cancelled: data?.filter(r => r?.status === 'cancelled')?.length || 0,
      today: data?.filter(r => {
        const today = new Date()?.toISOString()?.split('T')?.[0];
        const createdDate = new Date(r.created_at)?.toISOString()?.split('T')?.[0];
        return createdDate === today;
      })?.length || 0
    };

    return stats;
  } catch (error) {
    console.error('Error fetching reservation stats:', error);
    throw error;
  }
};

// Subscribe to realtime reservation changes
export const subscribeToReservations = (callback) => {
  const subscription = supabase?.channel('fatou_reservations_changes')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'fatou_reservations'
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return subscription;
};

// Unsubscribe from realtime changes
export const unsubscribeFromReservations = (subscription) => {
  if (subscription) {
    supabase?.removeChannel(subscription);
  }
};